import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const MAX_DAYS = 366;
const HALF_DAY = 0.5;

export const CYCLE = { CURRENT: "current", NEXT: "next" };
export const UNIT = { HALF_DAY: "half_day", DAYS: "days" };
export const OPERATION = { ADD: "add", SUBTRACT: "subtract" };
export const TARGET = { AVAILABLE: "available", ACQUIRED: "acquired" };

const INITIAL_FORM = {
  cycle: CYCLE.CURRENT,
  unit: UNIT.DAYS,
  days: "",
  operation: OPERATION.ADD,
  target: TARGET.AVAILABLE,
  reason: "",
};

const formatDate = (date) => date.toLocaleDateString("pt-PT");

const isValidDays = (value) =>
  Number.isFinite(value) && value > 0 && value <= MAX_DAYS && Number.isInteger(value * 2);

const getErrorMessage = (error, fallback) => {
  const detail = error?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg).filter(Boolean).join(" | ") || fallback;
  return typeof error === "string" ? error : fallback;
};

const AbsenceAdjustmentModal = ({ show, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(INITIAL_FORM);
      setError("");
    }
  }, [show]);

  const today = new Date();
  const year = today.getFullYear();
  const cycleOptions = [
    {
      value: CYCLE.CURRENT,
      label: t("components.employeeProfile.absencesTab.adjustment.cycleRange", {
        start: formatDate(today),
        end: formatDate(new Date(year, 11, 31)),
      }),
    },
    {
      value: CYCLE.NEXT,
      label: t("components.employeeProfile.absencesTab.adjustment.cycleRange", {
        start: formatDate(new Date(year + 1, 0, 1)),
        end: formatDate(new Date(year + 1, 11, 31)),
      }),
    },
  ];

  const isHalfDay = form.unit === UNIT.HALF_DAY;
  const update = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  // "Half day" locks the value at 0.5.
  const setUnit = (unit) =>
    setForm((prev) => ({ ...prev, unit, days: unit === UNIT.HALF_DAY ? String(HALF_DAY) : "" }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const days = isHalfDay ? HALF_DAY : Number(form.days);
    if (form.days === "" || !isValidDays(days)) {
      setError(t("components.employeeProfile.absencesTab.adjustment.invalidDays"));
      return;
    }

    setError("");
    setIsSaving(true);
    try {
      await onSubmit({
        cycle: form.cycle,
        target: form.target,
        operation: form.operation,
        days,
        reason: form.reason.trim(),
      });
      onClose();
    } catch (submitError) {
      setError(getErrorMessage(submitError, t("components.employeeProfile.absencesTab.adjustment.saveError")));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={isSaving ? undefined : onClose} centered>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title className="fw-bold" style={{ fontSize: "1.1rem" }}>
            {t("components.employeeProfile.absencesTab.adjustment.modalTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <Form.Group controlId="adjustment-cycle">
            <Form.Label className="fw-semibold">
              {t("components.employeeProfile.absencesTab.adjustment.cycle")}
            </Form.Label>
            <Form.Select value={form.cycle} onChange={update("cycle")}>
              {cycleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <div className="d-flex gap-4 mb-2">
              <Form.Check
                type="radio"
                id="adjustment-unit-half-day"
                name="adjustment-unit"
                label={t("components.employeeProfile.absencesTab.adjustment.halfDay")}
                checked={isHalfDay}
                onChange={() => setUnit(UNIT.HALF_DAY)}
              />
              <Form.Check
                type="radio"
                id="adjustment-unit-days"
                name="adjustment-unit"
                label={t("components.employeeProfile.absencesTab.adjustment.days")}
                checked={!isHalfDay}
                onChange={() => setUnit(UNIT.DAYS)}
              />
            </div>
            <Form.Control
              type="number"
              step="0.5"
              min="0.5"
              max={MAX_DAYS}
              value={form.days}
              onChange={update("days")}
              disabled={isHalfDay}
              isInvalid={Boolean(error) && form.days === ""}
              aria-label={t("components.employeeProfile.absencesTab.adjustment.value")}
              placeholder={t("components.employeeProfile.absencesTab.adjustment.value")}
            />
          </Form.Group>

          <Form.Group>
            <div className="d-flex gap-4">
              <Form.Check
                type="radio"
                id="adjustment-operation-add"
                name="adjustment-operation"
                label={t("components.employeeProfile.absencesTab.adjustment.add")}
                checked={form.operation === OPERATION.ADD}
                onChange={() => setForm((prev) => ({ ...prev, operation: OPERATION.ADD }))}
              />
              <Form.Check
                type="radio"
                id="adjustment-operation-subtract"
                name="adjustment-operation"
                label={t("components.employeeProfile.absencesTab.adjustment.subtract")}
                checked={form.operation === OPERATION.SUBTRACT}
                onChange={() => setForm((prev) => ({ ...prev, operation: OPERATION.SUBTRACT }))}
              />
            </div>
          </Form.Group>

          <Form.Group controlId="adjustment-target">
            <Form.Label className="fw-semibold">
              {t("components.employeeProfile.absencesTab.adjustment.target")}
            </Form.Label>
            <Form.Select value={form.target} onChange={update("target")}>
              <option value={TARGET.AVAILABLE}>{t("components.employeeProfile.absencesTab.availableDays")}</option>
              <option value={TARGET.ACQUIRED}>{t("components.employeeProfile.absencesTab.acquiredDays")}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="adjustment-reason">
            <Form.Label className="fw-semibold">
              {t("components.employeeProfile.absencesTab.adjustment.reason")}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={500}
              value={form.reason}
              onChange={update("reason")}
            />
          </Form.Group>

          {error && (
            <div className="alert alert-danger mb-0" role="alert">
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={isSaving}>
            {t("components.employeeProfile.absencesTab.cancel")}
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving && <Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />}
            {t("components.employeeProfile.absencesTab.save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AbsenceAdjustmentModal;
